import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { UserLogin } from '../AuthClasses';
import { Button, Groupper, Header, Input, Message, Checkbox, IconName, Field, Modal } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthHooks';
import { bindClick } from '@cocoan/components/Util';
import { useValidator } from '@cocoan/components/Hooks';
import { SetLoading } from '@cocoan/components/Classes';
import API, { CDN_URL, DEBUG } from '../AuthAPI';
import classNames from 'classnames';
import AccountConfirm from './AccountConfirm';
import '../scss/login.scss';
import 'fomantic-ui-css/components/dropdown.min.css';
import 'fomantic-ui-css/components/transition.min.css';
import 'fomantic-ui-css/components/icon.min.css';

enum LoginMode{
	LOGIN = 1,
	FORGOT = 2,
	TOTP = 3,
	CHANGE = 4,
	RECOVER = 5,
	CREATE = 6,
	ACCOUNT_CREATED = 7,
}

export enum Gender {
	MASCULINO = 1,
	FEMENINO = 2,
	OTRO = 3
}

interface LoginFormData{
	email: string,
	forgot_email: string,
	password: string,
	totp: string,
	code: string,
	captcha: string,
	remember: boolean,
}

interface PasswordForm{
	user?: {
		user_id: number,
		admin: boolean,
		email: string,
		birthday: number,
		gender_id: number,
		email_confirmed: boolean
	},
	token?: string,
	new_password: string,
	repeat_password: string,
}

interface ChangePasswordForm extends PasswordForm{
	current_password: string,
}

export interface CreateFormData{
	first_name: string,
	last_name: string,
	email: string,
	phone: string,
	password: string,
	captcha?: string,
	repeat_password: string,
	birthday: number,
	gender_id: number,
	privacy_accepted: boolean,
}

var ChangePasswordMessage = (props: { email: string, admin?: boolean, expired?: boolean })=>{
	return (
		<Message style={{ marginBottom: 15 }} type="info" text={`${props.expired ? 'La contraseña de COCOAN de esta cuenta ha sido marcada como expirada.' : ''} Favor de ingresar una nueva contraseña`} list={[
			`Usuario: ${props.email}`,
			props.admin ? (
				'Por seguridad, la contraseña debe de ser de mínimo 8 caracteres y debe de tener por lo menos: 1 número, 1 letra mayúscula, 1 letra minúscula y 1 caracter especial (!@#$%^&*.,+).'
			) : (
				'La contraseña debe de ser de mínimo 8 caracteres'
			)
		]} />
	)
}

const bdayMonths = [
	{ text: 'Enero', value: 0 },
	{ text: 'Febrero', value: 1 },
	{ text: 'Marzo', value: 2 },
	{ text: 'Abril', value: 3 },
	{ text: 'Mayo', value: 4 },
	{ text: 'Junio', value: 5 },
	{ text: 'Julio', value: 6 },
	{ text: 'Agosto', value: 7 },
	{ text: 'Septiembre', value: 8 },
	{ text: 'Octubre', value: 9 },
	{ text: 'Noviembre', value: 10 },
	{ text: 'Diciembre', value: 11 },
];
const bdayCurrentYear = moment().year();
const bdayYears = Array.from({length: 100}, (_, i) => ({ text: (bdayCurrentYear - i).toString(), value: bdayCurrentYear - i }));

var Login = (props: { frame?: boolean })=>{
	var { frame } = props;
	var { user, user_saved, clearSaved, loginSaved, loggedIn, getCaptcha } = useAuth();
	var [query, setQuery] = useSearchParams();
	var [mode, setMode] = useState<LoginMode>(frame ? LoginMode.CREATE : LoginMode.LOGIN);
	var [sending, setSending] = useState<boolean>(false);
	var [login, setLogin] = useState<UserLogin>(null);
	var [loginError, setLoginError] = useState<string>(null);
	var [shownError, setShownError] = useState<string>(null);
	var [shownMessage, setShownMessage] = useState<{ icon: IconName, title: string, substitle?: string }>(null);
	var [forgotSent, setForgotSent] = useState<boolean>(false);
	var [successMessage, setSuccessMessage] = useState<string>(null);
	var [privacyModal, setPrivacyModal] = useState<boolean>(false);
	var ChangeForm = useValidator<ChangePasswordForm>({
		current_password: '',
		new_password: '',
		repeat_password: '',
	}, {
		current_password: [{ rule: 'minLength', params: [5] }],
		new_password: [{ rule: DEBUG ? 'empty' : 'password' }],
		repeat_password: [{ rule: 'matches', params: ['new_password'], prompt: 'Las contraseñas no concuerdan' }],
	});
	var RecoverForm = useValidator<PasswordForm>({
		new_password: '',
		repeat_password: '',
	}, {
		new_password: [{ rule: DEBUG ? 'empty' : 'password' }],
		repeat_password: [{ rule: 'matches', params: ['new_password'], prompt: 'Las contraseñas no concuerdan' }],
	});
 	var LoginForm = useValidator<LoginFormData>({
		email: '',
		forgot_email: '',
		password: '',
		totp: '',
		code: '',
		captcha: null,
		remember: false,
	}, {
		email: [{
			rule: 'email',
			params: [5],
			prompt: `El correo electrónico no es válido`
		}],
	});
	var CreateForm = useValidator<CreateFormData>({
		first_name: '',
		last_name: '',
		email: '',
		phone: '',
		password: '',
		repeat_password: '',
		captcha: '',
		birthday: null,
		gender_id: null,
		privacy_accepted: false,
	}, {
		first_name: [{ rule: 'length', params: [3, 32], label: 'Nombre' }],
		last_name: [{ rule: 'length', params: [3, 64], label: 'Apellido' }],
		email: [{ rule: 'email', label: 'Correo electrónico' }],
		phone: [{ rule: 'length', params: [8, 10], label: 'Teléfono' }],
		password: [{ rule: 'length', params: [8, 128], label: 'Contraseña' }],
		repeat_password: [{ rule: 'matches', params: ['password'], prompt: 'Las contraseñas no concuerdan.' }],
		birthday: [{ rule: 'empty', label: 'Fecha de nacimiento' }],
		gender_id: [{ rule: 'empty', label: 'Género' }],
	})

	var initialBday = useMemo(() => CreateForm.data.birthday ? moment.unix(CreateForm.data.birthday) : null, []);
	var [bdayDay, setBdayDay] = useState<number | undefined>(initialBday ? initialBday.date() : undefined);
	var [bdayMonth, setBdayMonth] = useState<number | undefined>(initialBday ? initialBday.month() : undefined);
	var [bdayYear, setBdayYear] = useState<number | undefined>(initialBday ? initialBday.year() : undefined);

	useEffect(() => {
		if (bdayDay !== undefined && bdayMonth !== undefined && bdayYear !== undefined) {
			var m = moment().startOf('day').year(bdayYear).month(bdayMonth).date(bdayDay);
			if (CreateForm.data.birthday !== m.unix()) {
				CreateForm.setData(prev => ({ ...prev, birthday: m.unix() }));
			}
		} else {
			if (CreateForm.data.birthday !== null) {
				CreateForm.setData(prev => ({ ...prev, birthday: null }));
			}
		}
	}, [bdayDay, bdayMonth, bdayYear]);

	var bdayDaysCount = 31;
	if (bdayMonth !== undefined && bdayYear !== undefined) {
		bdayDaysCount = moment().year(bdayYear).month(bdayMonth).daysInMonth();
	} else if (bdayMonth !== undefined) {
		bdayDaysCount = moment().year(2000).month(bdayMonth).daysInMonth();
	}
	var bdayDays = Array.from({length: bdayDaysCount}, (_, i) => ({ text: (i + 1).toString(), value: i + 1 }));

	var pdv_token = query.get('pdv');
	var recover_token = query.get('tkn');
	var confirm_token = query.get('ct');
	var is_pdv = pdv_token==='1';
	var auth_url = decodeURIComponent(query.get('continue') || '');
	var is_admin = query.get('admin')==='1';
	var is_frame = frame || window.location.pathname.endsWith('/frame');
	var valid_continue = (!!auth_url && auth_url.length>=10) || is_frame;
	var params_valid = (valid_continue && (!pdv_token || pdv_token==='1')) || (recover_token?.length>10);

	var isRecoverTokenValid = ()=>{
		API.verifyToken(recover_token).then(res=>{
			if(res.error){
				setQuery(q=>{
					q.delete('tkn');
					return q;
				});
				setMode(LoginMode.LOGIN);
				return setShownError(res.message);
			}
			RecoverForm.setData({
				user: {
					user_id: res.data.user_id,
					admin: res.data.admin,
					email: res.data.email,
					birthday: res.data.birthday,
					gender_id: res.data.gender_id,
					email_confirmed: res.data.email_confirmed,
				},
				token: recover_token,
				new_password: '',
				repeat_password: '',
			});
			setMode(LoginMode.RECOVER);
		}).catch(()=>{
			setMode(LoginMode.LOGIN);
			setShownError('El token es invalido o se vencío (LCL-RCV-2)');
		})
	}

	useEffect(()=>{
		var recover_token = query.get('tkn');
		if(recover_token && params_valid && recover_token.length>10){
			setMode(LoginMode.RECOVER)
			isRecoverTokenValid()
		}else if(confirm_token){

		}
	}, []);

	useEffect(()=>{
		setLoginError(null);
	}, [mode]);

	var redirect_url = useMemo(()=>query.get('redirect'), [query]);
   var getContinueUrl = (data: UserLogin)=>{
      if(!auth_url || !data || !data.tokens) return null;
      if(auth_url.endsWith('/') && !auth_url.endsWith('://')) auth_url = auth_url.substring(0, auth_url.length-1);
      var params : any = {
         at: data.tokens.access,
         e: data.tokens.expires,
         user: encodeURIComponent(JSON.stringify(data.user)),
      }
      if(data.tokens.refresh){
         params.rt = data.tokens.refresh;
         params.re = data.tokens.refresh_expires;
      }      
      if(redirect_url && redirect_url.length>=10){
         params.r = redirect_url;
      }
      return `${auth_url}?${Object.keys(params).map(a=>`${a}=${params[a]}`).join('&')}`
   }
	

	var getButtonText = ()=>{
		switch(mode){
			case LoginMode.LOGIN: return 'Iniciar sesión';
			case LoginMode.CREATE: return 'Crear cuenta';
			default: return 'Continuar';
		}
	}

	var getHeaderText = ()=>{
		switch(mode){
			case LoginMode.CREATE:
			case LoginMode.ACCOUNT_CREATED: return 'Crear cuenta';
			default: return 'Iniciar sesión';
		}
	}

	var resetPrompts = ()=>{
		LoginForm.setPrompts(null);
		ChangeForm.setPrompts(null);
		RecoverForm.setPrompts(null);
		CreateForm.setPrompts(null);
		setSuccessMessage(null);
	}

	var refreshAuth = ()=>{
		if(!user_saved) return;
		setSending(true);
		loginSaved().then(res=>{
			if(res.error) return setLoginError(res.message);
			if(!res.data.tokens?.access){
				return setLoginError(`Hubo un error inesperado iniciando sesión (LCL-SVD-2)`);
			}
			setLogin(res.data);
			var continue_url = getContinueUrl(res.data);
			setTimeout(()=>{
				window.location.href = continue_url;
			}, is_pdv ? 300 : 1000);
		}).catch(err=>{
			setLoginError(`Hubo un error inesperado haciendo login. (LCL-SVD-1)`);
		}).finally(()=>{
			setSending(false);
		})
	}

	var submitLogin = async (setLoading: SetLoading)=>{
		var { valid } = LoginForm.validate(null, {
			password: [{
				rule: 'minLength', params: [6], prompt: 'La contraseña debe de ser de mínimo 6 caracteres', if: mode===LoginMode.LOGIN
			}]
		});
		if(!valid) return;

		setLoading(true);
		try{
			var captcha = await getCaptcha();
			if(!captcha){
				setLoading(false);
				return LoginForm.setPrompts(['Hubo un error realizando el captcha (LCL-ATHSB-1)']);
			}
		}catch(e){
			LoginForm.setPrompts(['Hubo un error realizando el captcha (LCL-ATHSB-2)']);
			setLoading(false);
			return;
		}

		var { data } = LoginForm
		var should_remember = is_pdv ? true : data.remember;
		try{
			var res = await API.login(data.email, data.password, captcha, should_remember, is_pdv, is_admin);
			if(res.error) return LoginForm.setPrompts([res.message]);
			if(res.data.change_password){
				ChangeForm.setData({
					user: {
						user_id: res.data.user.user_id,
						admin: res.data.change_password.admin,
						email: res.data.user.email,
						birthday: res.data.user.birthday,
						gender_id: res.data.user.gender_id,
						email_confirmed: res.data.user.email_confirmed,
					},
					token: res.data.change_password.token,
					current_password: '',
					new_password: '',
					repeat_password: '',
				});
				LoginForm.reset();
				return setMode(LoginMode.CHANGE);
			}
			if(!res.data.tokens?.access){
				return LoginForm.setPrompts(['Hubo un error inesperado iniciando sesión (LCL-ADMLG-2)']);
			}
			setLogin(res.data);
			var continue_url = getContinueUrl(res.data);
			setTimeout(()=>{
				window.location.href = continue_url;
			}, is_pdv ? 300 : 1000);
		}catch(err){
			LoginForm.setPrompts(['Hubo un error inesperado iniciando sesión (LCL-ADMLG-1)']);
		}finally{
			setLoading(false);
		}
	}

	var submitCreate = async (setLoading: SetLoading)=>{
		var { valid } = CreateForm.validate();
		if(!valid) return;

		setLoading(true);
		try{
			var captcha = await getCaptcha();
			if(!captcha){
				setLoading(false);
				return CreateForm.setPrompts(['Hubo un error realizando el captcha (LCL-ATHSB-1)']);
			}
		}catch(e){
			CreateForm.setPrompts(['Hubo un error realizando el captcha (LCL-ATHSB-2)']);
			setLoading(false);
			return;
		}

		CreateForm.setPrompts(null);
		API.createAccount({
			first_name: CreateForm.data.first_name,
			last_name: CreateForm.data.last_name,
			email: CreateForm.data.email,
			phone: CreateForm.data.phone,
			password: CreateForm.data.password,
			birthday: CreateForm.data.birthday,
			gender_id: Number(CreateForm.data.gender_id),
			captcha,
		}).then(res=>{
			if(res.error) return CreateForm.setPrompts([res.message]);
			if(frame){
				try{ window.parent.postMessage({ type: 'cocoan-account-created' }, '*'); }catch(e){}
			}
			setMode(LoginMode.ACCOUNT_CREATED);
		}).catch(err=>{
			CreateForm.setPrompts(['Hubo un error inesperado creando la cuenta (LCL-ATHCRA-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var submitForgot = async (setLoading: SetLoading)=>{
		var { valid } = LoginForm.validate(null, {
			email: [{
				rule: 'email', prompt: 'El correo electrónico no es válido',
			}]
		});
		if(!valid) return;

		setLoading(true);
		try{
			var captcha = await getCaptcha();
			if(!captcha){
				setLoading(false);
				return LoginForm.setPrompts(['Hubo un error realizando el captcha (LCL-ATHSB-1)']);
			}
		}catch(e){
			LoginForm.setPrompts(['Hubo un error realizando el captcha (LCL-ATHSB-2)']);
			setLoading(false);
			return;
		}

		LoginForm.setPrompts(null);
		API.sendForgot(LoginForm.data.email, captcha).then(res=>{
			if(res.error) return LoginForm.setPrompts([res.message]);
			setMode(LoginMode.LOGIN)
			setForgotSent(true);
		}).catch(err=>{
			LoginForm.setPrompts(['Hubo un error haciendo la solicitud de contraseña. (LCL-ATHFP-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var submitRecover = (setLoading: SetLoading)=>{
		var { valid } = RecoverForm.validate(null, {
			new_password: [{ 
				label: 'Nueva contraseña',
				rule: DEBUG ? 'length' : (RecoverForm.data.user.admin ? 'password' : 'length'),
				params: [8, 127], 
			}],
		})
		if(!valid) return;

		setLoading(true);
		RecoverForm.setPrompts(null);
		API.recoverPassword(recover_token, RecoverForm.data.new_password).then(res=>{
			if(res.error) return RecoverForm.setPrompts([res.message]);
			resetPrompts();
			RecoverForm.reset();
			setQuery(q=>{
				q.delete('tkn');
				return q;
			});
			if(valid_continue){
				setMode(LoginMode.LOGIN);
				setSuccessMessage('Se ha cambiado la contraseña, favor de iniciar sesión de nuevo.');
			}else{
				setShownMessage({
					icon: 'check-circle',
					title: 'Contraseña actualizada',
					substitle: 'Se ha cambiado la contraseña de la cuenta'
				});
			}
		}).catch(err=>{
			RecoverForm.setPrompts(['Hubo un error inesperado reestableciendo la contraseña (LCL-ATHRCV-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}
	
	var submitChange = (setLoading: SetLoading)=>{
		var { valid } = ChangeForm.validate(null, {
			new_password: [{ rule: ChangeForm.data.user.admin ? 'password' : 'length', params: [8, 127], label: 'Nueva contraseña' }],
		});
		if(!valid) return;

		setLoading(true);
		LoginForm.setPrompts(null);
		API.changePassword(ChangeForm.data.token, ChangeForm.data.current_password, ChangeForm.data.new_password).then(res=>{
			if(res.error) return ChangeForm.setPrompts([res.message]);
			resetPrompts();
			setMode(LoginMode.LOGIN);
			setSuccessMessage('Se ha cambiado la contraseña, favor de iniciar sesión de nuevo.');
		}).catch(err=>{
			ChangeForm.setPrompts(['Hubo un error inesperado cambiando la contraseña (LCL-ATHCHP-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var submit = async (setLoading: SetLoading)=>{
		if(mode===LoginMode.CHANGE) return submitChange(setLoading);
		if(mode===LoginMode.RECOVER) return submitRecover(setLoading);
		if(mode===LoginMode.LOGIN) return submitLogin(setLoading);
		if(mode===LoginMode.FORGOT) return submitForgot(setLoading);
		if(mode===LoginMode.CREATE) return submitCreate(setLoading);
	}

	var returnMode = ()=>{
		LoginForm.reset();
		ChangeForm.reset();
		RecoverForm.reset();
		CreateForm.reset();
		resetPrompts();
		setMode(LoginMode.LOGIN);
	}

	var actions_shown = mode!==LoginMode.LOGIN && mode!==LoginMode.ACCOUNT_CREATED && (mode!==LoginMode.RECOVER || RecoverForm?.data.user)

	if(frame){
		return <div className='co login-frame'>
			{mode===LoginMode.ACCOUNT_CREATED ? (
				<div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', minHeight: 300 }}>
					<Header 
						iconName='check-circle' 
						iconStyle={{ fontSize: 50, marginBottom: 10, color: '#1a9461' }} 
						text='¡Cuenta creada!' 
						subtext='Ahora puedes iniciar sesión con tu correo y contraseña.' 
						subheaderStyle={{ lineHeight: '16px', color: '#ffffff' }} 
					/>
				</div>
			) : <>
				<Input label='Nombre' error={CreateForm.errors.first_name} value={CreateForm.data.first_name} onChange={CreateForm.onDataChange('first_name')} />
				<Input label='Apellido' error={CreateForm.errors.last_name} value={CreateForm.data.last_name} onChange={CreateForm.onDataChange('last_name')} />
				<Input label='Correo electrónico' error={CreateForm.errors.email} value={CreateForm.data.email} onChange={CreateForm.onDataChange('email')} inputType='email' />
				<Input label='Teléfono' error={CreateForm.errors.phone} value={CreateForm.data.phone} onChange={CreateForm.onDataChange('phone')} inputType='tel' />
				<Field label='Fecha de nacimiento' style={{ width: '100%' }} error={CreateForm.errors.birthday}>
					<div style={{ display: 'flex', gap: 10 }}>
						<Dropdown fluid selection placeholder='Día' value={bdayDay} onChange={(_e, data) => setBdayDay(data.value as number)} options={bdayDays} style={{ minWidth: 70 }} />
						<Dropdown fluid selection placeholder='Mes' value={bdayMonth} onChange={(_e, data) => setBdayMonth(data.value as number)} options={bdayMonths} style={{ minWidth: 100 }} />
						<Dropdown fluid selection placeholder='Año' value={bdayYear} onChange={(_e, data) => setBdayYear(data.value as number)} options={bdayYears} style={{ minWidth: 80 }} />
					</div>
				</Field>
				<Field label='Género' style={{ width: '100%' }}>
						<Dropdown 
							fluid
							selection
							value={CreateForm.data.gender_id} 
							placeholder="Selecciona un género"
							onChange={(_e, data) => CreateForm.setData({...CreateForm.data, gender_id: data.value as number})}
							options={[
								{ text: 'Masculino', value: Gender.MASCULINO },
								{ text: 'Femenino', value: Gender.FEMENINO },
								{ text: 'Otro', value: Gender.OTRO },
							]} 
						/>
				</Field>
				<Input label='Contraseña' error={CreateForm.errors.password} value={CreateForm.data.password} onChange={CreateForm.onDataChange('password')} inputType='password' />
				<Input label='Confirmar contraseña' error={CreateForm.errors.repeat_password || CreateForm.errors.password} value={CreateForm.data.repeat_password} inputType='password' onChange={CreateForm.onDataChange('repeat_password')} />
				<div style={{ marginTop: 15, marginBottom: 5, width: '100%', minHeight: 30, display: 'flex', alignItems: 'center' }}>
					<div 
						style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} 
						onClick={() => CreateForm.onDataChange('privacy_accepted')(!CreateForm.data.privacy_accepted)}
					>
						<div style={{
							width: 18, 
							height: 18, 
							borderRadius: 4, 
							border: '2px solid',
							borderColor: CreateForm.data.privacy_accepted ? '#402312' : '#facc15',
							backgroundColor: CreateForm.data.privacy_accepted ? '#402312' : '#facc15',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							marginRight: 10,
							flexShrink: 0
						}}>
							{CreateForm.data.privacy_accepted && <i className="check icon" style={{ color: 'white', fontSize: 12, margin: 0 }} />}
						</div>
						<span style={{ fontSize: 13, color: 'white', userSelect: 'none' }}>
							He leído y acepto el <a href="/privacy" target="_blank" rel="noreferrer" onClick={(e)=>e.stopPropagation()} style={{ cursor: 'pointer', textDecoration: 'underline', color: 'white' }}>Aviso de Privacidad</a>
						</span>
					</div>
				</div>
				<Message type='error' list={CreateForm.prompts} style={{ marginTop: 15, marginBottom: -10 }} />
				<Button color='yellow' text='Crear cuenta' fluid style={{ marginTop: 20 }} onClick={submit} disabled={!CreateForm.data.privacy_accepted} />
			</>}
		</div>
	}

	return <div className={classNames("co login-screen", {
		change: mode===LoginMode.CHANGE
	})} style={{ ['--mobile-bg' as any]: `url(${process.env.PUBLIC_URL}/IMG_1080X1950.png)` }}>
		<div className="image" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/IMG_638x768.png)` }}></div>
		<div className="form">
			<img className='logo' src={`${CDN_URL}/static/logo/Logo_Black_Small.png`} alt="COCOAN" />
			{shownError ? <>
				<Header iconName='exclamation-triangle' iconStyle={{ fontSize: 70, marginBottom: 15 }} text='Error' subtext={shownError} />
				<Button text='Regresar' style={{ marginTop: 15, minWidth: 200 }} onClick={()=>setShownError(null)} loading={false} />
			</> : shownMessage ? (
				<Header iconName={shownMessage.icon} iconStyle={{ fontSize: 70, marginBottom: 15 }} text={shownMessage.title} subtext={shownMessage.substitle} />
			) : (login && (!!user || is_pdv)) ? (
				<Header
					iconName='check-circle' 
					iconStyle={{ color: '#1a9461', marginBottom: 10 }} 
					text={`¡Hola, ${(login?.user?.first_name || 'Usuario')}!`}
					subtext='Te redireccionaremos pronto a tu destino'
					style={{ display: 'flex' , flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginTop: 50  }}
					subheaderStyle={{ marginTop: 5 }}
					actions={(
						<a href={getContinueUrl(login)} rel='noreferrer' style={{ cursor: 'pointer' }}>
							<Button text='Continuar' size='tiny' style={{ padding: '2px 10px', minWidth: 100 }} basic color='green' />
						</a>
					)}
				/>
			) : params_valid ? <>
				{mode!==LoginMode.LOGIN && (
					<div className="head">
						<Header centered text={getHeaderText()} subtext={mode===LoginMode.FORGOT ? 'Olvidé mi contraseña' : null} />
					</div>
				)}
				{!!successMessage && (
					<Message type="success" style={{ marginBottom: 15 }}>
						{successMessage}
					</Message>
				)}
				{mode===LoginMode.ACCOUNT_CREATED ? <>
					<Header 
						iconName='check-circle' 
						iconStyle={{ fontSize: 70, marginBottom: 10 }} 
						text='Cuenta creada' 
						subtext={`¡Tu cuenta COCOAN ha sido creada con éxito!`} 
						subheaderStyle={{ lineHeight: '16px' }} 
					/>
					<Message type='success' style={{ marginTop: 15, textAlign: 'center' }}>
						Te hemos enviado un correo de confirmación. Por favor, verifica tu bandeja de entrada (y la carpeta de spam) y confirma tu cuenta antes de iniciar sesión.
					</Message>
					<Button text='Iniciar sesión' color='black' style={{ marginTop: 15, minWidth: 200 }} onClick={returnMode} loading={false} />
				</> : (user_saved && !!user && !is_pdv) ? (
					loginError ? <>
						<Header iconName='exclamation-triangle' iconStyle={{ fontSize: 70, marginBottom: 15 }} text='Error' subtext={loginError} />
						<Button text='Regresar' style={{ marginTop: 15, minWidth: 200 }} onClick={()=>setLoginError(null)} loading={false} />
					</> : <div style={{ display: 'flex', flexDirection: 'column', gap: 5, paddingTop: 20 }}>
						<Header
							iconName='user-circle' 
							iconStyle={{ marginBottom: 10 }} 
							text={`${user.user.first_name} ${user.user.last_name}`} 
							subtext='Guardamos tu sesión'
							style={{ display: 'flex' , flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}
						/>
						<Button text='Iniciar sesión' style={{ marginTop: 15, minWidth: 200 }} color='yellow' onClick={refreshAuth} loading={sending} />
						<Button text='Cerrar sesión' size='tiny' style={{ marginTop: 5, minWidth: 120 }} basic color='black' onClick={clearSaved} disabled={sending} />
					</div>
				) : forgotSent ? <>
					<Header 
						iconName='envelope' 
						iconStyle={{ fontSize: 70, marginBottom: 10 }} 
						text='Enlace enviado' 
						subtext={`Si existe una cuenta de COCOAN con este correo electrónico, te enviamos un correo con instrucciones para restablecer tu contraseña`} 
						subheaderStyle={{ lineHeight: '16px' }} 
					/>
					<Button text='Iniciar sesión' color='black' style={{ marginTop: 20, minWidth: 200 }} onClick={()=>{
						setForgotSent(null);
						setMode(LoginMode.LOGIN);
					}} loading={false} />
				</> : mode===LoginMode.RECOVER && RecoverForm.data?.user===null ? (
					<Header 
						loading
						iconStyle={{ fontSize: 70, marginBottom: 10 }} 
						text='Cargando enlace' 
						subheaderStyle={{ lineHeight: '16px' }} 
					/>
				) : <div className='contents'>
					{mode===LoginMode.LOGIN ? <div className='login'>
						<Input label='Correo electrónico' error={LoginForm.errors.email} value={LoginForm.data.email} onChange={LoginForm.onDataChange('email')} />
						<Input label='Contraseña' style={{ marginBottom: 0 }} error={LoginForm.errors.password} value={LoginForm.data.password} onChange={LoginForm.onDataChange('password')} inputType='password' submitOnEnter onSubmit={submit} />
						{!is_pdv && (
							<div className="remember-row" style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
								<Checkbox label={'Recordar mi sesión'} style={{ marginTop: 0 }} checked={LoginForm.data.remember} onChange={LoginForm.onDataChange('remember')} color='black' />
								<Button text='¿Olvidaste tu contraseña?' size='tiny' basic color='black' onClick={bindClick(setMode, LoginMode.FORGOT)} />
							</div>
						)}
						<Message type='error' list={LoginForm.prompts} style={{ marginTop: 15, marginBottom: -5 }} />
						<Button color='yellow' text='Iniciar sesión' fluid style={{ marginTop: 20 }} onClick={submit} />
						{!is_pdv && <>
							<Groupper.Divider type='line text' text={'¿No tienes una cuenta?'} />
							<Button text='Crear cuenta' fluid iconName={'plus'} onClick={bindClick(setMode, LoginMode.CREATE)} />
						</>}
					</div> : mode===LoginMode.FORGOT ? <div className='forgot'>
						<Message centered type='info' text={`Ingresa el correo electrónico de tu cuenta de COCOAN`} style={{ marginBottom: 15 }} />
						<Input label={'Correo electrónico'} error={LoginForm.errors.email} value={LoginForm.data.email} onChange={LoginForm.onDataChange('email')} />
						<Message type='error' list={LoginForm.prompts} style={{ marginTop: 15, marginBottom: -5 }} />
					</div> : mode===LoginMode.CHANGE ? <div className='change'>
						<ChangePasswordMessage admin={ChangeForm.data.user.admin} email={ChangeForm.data.user.email} />
						<Input label='Contraseña actual' error={ChangeForm.errors.current_password} value={ChangeForm.data.current_password} onChange={ChangeForm.onDataChange('current_password')} inputType='password' />
						<Input label='Nueva contraseña' error={ChangeForm.errors.new_password} value={ChangeForm.data.new_password} onChange={ChangeForm.onDataChange('new_password')} inputType='password' />
						<Input label='Repetir nueva contraseña' error={ChangeForm.errors.repeat_password} value={ChangeForm.data.repeat_password} onChange={ChangeForm.onDataChange('repeat_password')} inputType='password' />
						<Message type='error' list={ChangeForm.prompts} style={{ marginTop: 15, marginBottom: -5 }} />
					</div> : mode===LoginMode.RECOVER ? (
						RecoverForm.data.user ? <>
							<ChangePasswordMessage admin={RecoverForm.data.user?.admin} email={RecoverForm.data.user?.email} />
							<Input label='Nueva contraseña' error={RecoverForm.errors.new_password} value={RecoverForm.data.new_password} onChange={RecoverForm.onDataChange('new_password')} inputType='password' />
							<Input label='Repetir nueva contraseña' error={RecoverForm.errors.repeat_password} value={RecoverForm.data.repeat_password} onChange={RecoverForm.onDataChange('repeat_password')} inputType='password' />
							<Message type='error' list={RecoverForm.prompts} style={{ marginTop: 15, marginBottom: -5 }} />
						</> : (
							<Header text={'Cargando datos'} loading />
						)
					) : mode===LoginMode.CREATE ? <>
						<Input label='Nombre' error={CreateForm.errors.first_name} value={CreateForm.data.first_name} onChange={CreateForm.onDataChange('first_name')} />
						<Input label='Apellido' error={CreateForm.errors.last_name} value={CreateForm.data.last_name} onChange={CreateForm.onDataChange('last_name')} />
						<Input label='Correo electrónico' error={CreateForm.errors.email} value={CreateForm.data.email} onChange={CreateForm.onDataChange('email')} inputType='email' />
						<Input label='Teléfono' error={CreateForm.errors.phone} value={CreateForm.data.phone} onChange={CreateForm.onDataChange('phone')} inputType='tel' />
						
						<Field label='Fecha de nacimiento' style={{ width: '100%' }} error={CreateForm.errors.birthday}>
							<div style={{ display: 'flex', gap: 10 }}>
								<Dropdown fluid selection placeholder='Día' value={bdayDay} onChange={(_e, data) => setBdayDay(data.value as number)} options={bdayDays} style={{ minWidth: 70 }} />
								<Dropdown fluid selection placeholder='Mes' value={bdayMonth} onChange={(_e, data) => setBdayMonth(data.value as number)} options={bdayMonths} style={{ minWidth: 100 }} />
								<Dropdown fluid selection placeholder='Año' value={bdayYear} onChange={(_e, data) => setBdayYear(data.value as number)} options={bdayYears} style={{ minWidth: 80 }} />
							</div>
						</Field>
						<Field label='Género'>
							<Dropdown 
								fluid
								selection
								value={CreateForm.data.gender_id} 
								placeholder="Selecciona un género"
								onChange={(_e, data) => CreateForm.setData({...CreateForm.data, gender_id: data.value as number})}
								options={[
									{ text: 'Masculino', value: Gender.MASCULINO },
									{ text: 'Femenino', value: Gender.FEMENINO },
									{ text: 'Otro', value: Gender.OTRO },
								]} 
							/>
						</Field>

						<Groupper.Divider type='dashed' />
						<Input label='Contraseña' error={CreateForm.errors.password} value={CreateForm.data.password} onChange={CreateForm.onDataChange('password')} inputType='password' />
						<Input label='Confirmar contraseña' error={CreateForm.errors.repeat_password || CreateForm.errors.password} value={CreateForm.data.repeat_password} inputType='password' onChange={CreateForm.onDataChange('repeat_password')} />
						<div style={{ marginTop: 15, marginBottom: 5, width: '100%', minHeight: 30, display: 'flex', alignItems: 'center' }}>
							<div 
								style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} 
								onClick={() => CreateForm.onDataChange('privacy_accepted')(!CreateForm.data.privacy_accepted)}
							>
								<div style={{
									width: 18, 
									height: 18, 
									borderRadius: 4, 
									border: '2px solid',
									borderColor: CreateForm.data.privacy_accepted ? '#402312' : '#facc15',
									backgroundColor: CreateForm.data.privacy_accepted ? '#402312' : '#facc15',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									marginRight: 10,
									flexShrink: 0
								}}>
									{CreateForm.data.privacy_accepted && <i className="check icon" style={{ color: 'white', fontSize: 12, margin: 0 }} />}
								</div>
								<span style={{ fontSize: 13, color: 'black', userSelect: 'none' }}>
									He leído y acepto el <a onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setPrivacyModal(true); }} style={{ cursor: 'pointer', textDecoration: 'underline', color: 'black' }}>Aviso de Privacidad</a>
								</span>
							</div>
						</div>
						<Message type='error' list={CreateForm.prompts} style={{ marginTop: 15, marginBottom: -10 }} />
					</> : null}
				</div>}
			</> : confirm_token ? (
				<AccountConfirm token={confirm_token} />
			) : (
				<Header iconName='exclamation-triangle' iconStyle={{ fontSize: 70, marginBottom: 15 }} text='Autenticación inválida' subtext='La autenticación de usuario fue mal configurada' />
			)}
			{!!actions_shown && (
				<div className='actions'>
					<Button basic text={'Regresar'} onClick={returnMode} style={{ marginRight: 5 }} />
					<Button className='ok' color='black' text={getButtonText()} onClick={submit} disabled={mode===LoginMode.CREATE && !CreateForm.data.privacy_accepted} />
				</div>
			)}
		</div>
		<Modal open={privacyModal} onClose={()=>setPrivacyModal(false)} size='tiny'>
			<Header iconName='shield-alt' text='Aviso de Privacidad' />
			<Modal.Content style={{ fontSize: 14, lineHeight: '1.6' }}>
				<p>En Cocoan Chocolatería, valoramos y protegemos la privacidad de nuestros clientes. Los datos personales que nos proporcione, como nombre, teléfono, correo electrónico, dirección de envío y datos de facturación, serán utilizados para procesar sus pedidos, gestionar el envío de productos, emitir comprobantes fiscales, brindar atención al cliente y dar seguimiento a sus compras.</p>
				<p>De manera adicional, y únicamente con su consentimiento, podremos utilizar sus datos para enviar información sobre promociones, novedades y campañas comerciales. Usted podrá solicitar en cualquier momento dejar de recibir este tipo de comunicaciones.</p>
				<p>Sus datos personales únicamente podrán ser compartidos con proveedores que intervengan en el procesamiento de pagos, servicios de mensajería o cuando exista una obligación legal para ello.</p>
				<p>Usted podrá ejercer sus derechos de Acceso, Rectificación, Cancelación y Oposición (ARCO), así como solicitar información sobre el tratamiento de sus datos personales, mediante los medios de contacto que se publiquen en este sitio.</p>
				<p>Nuestro sitio web puede utilizar cookies y tecnologías similares para mejorar la experiencia de navegación y obtener información estadística sobre el uso del sitio.</p>
				<p>Cualquier modificación al presente Aviso de Privacidad será publicada oportunamente en este mismo sitio web.</p>
				<p><strong>Actualización: 2026</strong></p>
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={()=>setPrivacyModal(false)} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default Login;